import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select, { type MultiValue } from 'react-select';
import type { ClientType } from '@econtractor/types';
import * as apiClient from 'api/apiClients';
import { useTheme } from 'context/ThemeContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import { TeamSelect } from 'modules/user';
import { ClientSelected } from 'modules/client';
import {
    defaultObjectStatuses,
    defaultSaleStatuses,
    useObject,
} from '../context/ObjectProvider';
import vconst from 'utils/helpers/structs/vconst';
import { isSameIdSet } from 'utils/helpers/list/isSameIdSet';
import SolarIcon from 'components/icons/SolarIcon';
import EToolTip from 'components/others/EToolTip';

type StatusOption = {
    value: number;
    label: string;
};

const statusIcons: Record<string, ReactNode> = {
    '1': <SolarIcon name="play-circle" className="fs-14 text-success" />,
    '0': <SolarIcon name="pause-circle" className="fs-14 text-warning" />,
    '99': <SolarIcon name="check-circle" className="fs-14 text-success" />,
    '100': <SolarIcon name="check-circle-fill" className="fs-14 text-success" />,
    '-1': <SolarIcon name="x-circle-fill" className="fs-14 text-danger" />,
};

const ObjectsFilter = () => {
    const { t } = useTranslation(['Objects', 'common']);
    const { darkTheme } = useTheme();
    const {
        onlySale,
        onlyService,
        isSaleLike,
        filterStatus,
        setFilterStatus,
        filterTeamId,
        setFilterTeamId,
        filterClientId,
        setFilterClientId,
    } = useObject();
    const [draftClient, setDraftClient] = useState<ClientType | null>(null);
    const [show, setShow] = useState(false);
    const [draftStatus, setDraftStatus] = useState<number[]>([...filterStatus]);
    const [draftTeamId, setDraftTeamId] = useState<number | null>(filterTeamId);
    const [draftClientId, setDraftClientId] = useState<number | null>(filterClientId);
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;
    const defaultStatuses = isSaleLike ? defaultSaleStatuses : defaultObjectStatuses;
    const statusSource = isSaleLike ? vconst.saleAllStatuses : vconst.objectAllStatuses;
    const title = onlyService
        ? t('toolServiceSaleFilter')
        : onlySale
          ? t('toolSaleFilter')
          : t('toolObjectFilter');

    const filterActive =
        !isSameIdSet(filterStatus, defaultStatuses)
        || (!isSaleLike && Boolean(filterTeamId))
        || Boolean(filterClientId);

    const statusOptions = useMemo<StatusOption[]>(
        () =>
            statusSource.map((oStatus: number) => ({
                value: oStatus,
                label: t(`status.${oStatus}`),
            })),
        [t, statusSource],
    );

    const selectedStatuses = useMemo(
        () => statusOptions.filter((opt) => draftStatus.includes(opt.value)),
        [draftStatus, statusOptions],
    );

    useEffect(() => {
        if (!show) return;
        if (!draftClientId) {
            setDraftClient(null);
            return;
        }
        if (draftClient?.ID === draftClientId) return;

        let cancelled = false;
        (async () => {
            const resClient = await apiClient.getClientById(draftClientId);
            if (cancelled) return;
            setDraftClient(resClient || null);
            if (!resClient) setDraftClientId(null);
        })();

        return () => {
            cancelled = true;
        };
    }, [show, draftClientId, draftClient?.ID]);

    const handleHide = () => setShow(false);

    const handleOpen = () => {
        setDraftStatus([...filterStatus]);
        setDraftTeamId(filterTeamId);
        setDraftClientId(filterClientId);
        setShow(true);
    };

    const handleChangeStatus = (options: MultiValue<StatusOption>) => {
        setDraftStatus((options || []).map((opt) => opt.value));
    };

    const handleChangeClient = (client: ClientType | null) => {
        setDraftClient(client);
        setDraftClientId(client?.ID ? Number(client.ID) : null);
    };

    const handleApply = () => {
        setFilterStatus([...draftStatus]);
        if (!isSaleLike) setFilterTeamId(draftTeamId);
        setFilterClientId(draftClientId);
        handleHide();
    };

    const handleReset = () => {
        setFilterStatus([...defaultStatuses]);
        if (!isSaleLike) setFilterTeamId(null);
        setFilterClientId(null);
        handleHide();
    };

    const formatStatusOption = (option: StatusOption, { context }: { context: 'menu' | 'value' }) => {
        if (context === 'value') return option.label;
        return (
            <span className="d-inline-flex align-items-center gap-1">
                {statusIcons?.[String(option.value)]}
                <span>{option.label}</span>
            </span>
        );
    };

    return (
        <>
            <EToolTip title={title}>
                <span className="d-inline-block">
                    <Button
                        className="fs-14 px-2 ms-1"
                        size="sm"
                        variant={filterActive ? 'danger' : 'primary'}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            handleOpen();
                        }}
                    >
                        <SolarIcon name="funnel" className="fs-14 p-0" />
                    </Button>
                </span>
            </EToolTip>

            <Offcanvas show={show} onHide={handleHide} placement="end">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{title}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-filter-form d-flex flex-column h-100">
                        <div className="off-filter-content px-3">
                            <div className="mb-3">
                                <label className="form-label text-muted fs-12 mb-1">{t('filterStatus')}</label>
                                <Select<StatusOption, true>
                                    isMulti
                                    isClearable
                                    isSearchable={false}
                                    options={statusOptions}
                                    value={selectedStatuses}
                                    onChange={handleChangeStatus}
                                    placeholder={t('filterStatus')}
                                    className="w-100 object-filter-select"
                                    classNamePrefix="object-filter-select"
                                    styles={{
                                        ...selectTheme,
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 2100,
                                        }),
                                    }}
                                    formatOptionLabel={formatStatusOption}
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                    menuShouldScrollIntoView={false}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                />
                            </div>

                            {!isSaleLike ? (
                                <div className="mb-3">
                                    <label className="form-label text-muted fs-12 mb-1">{t('filterTeam')}</label>
                                    <TeamSelect
                                        hideLabel
                                        isClearable
                                        showEmpty={false}
                                        placeholder={t('filterTeam')}
                                        value={draftTeamId || null}
                                        onChange={(opt) => {
                                            setDraftTeamId(opt?.value ? Number(opt.value) : null);
                                        }}
                                    />
                                </div>
                            ) : null}

                            <div className="mb-3">
                                <ClientSelected
                                    title={t('filterClient')}
                                    className="mb-0"
                                    allowNew={false}
                                    clearable
                                    maxHeight={160}
                                    value={draftClient}
                                    onChange={handleChangeClient}
                                />
                            </div>
                        </div>
                        <div className="p-3 offcanvas-footer off-filter-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" onClick={handleReset}>
                                    {t('common:buttons.reset')}
                                </Button>
                                <Button variant="info" className="min100px" onClick={handleApply}>
                                    {t('common:buttons.apply')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default ObjectsFilter;
